/**
 * DuplicateChooser.tsx — the ONE renderer for the duplicate guard's candidates
 * (Slice C: C2's advisory tile and C3's refusal form), shared by every create
 * surface that can mint a duplicate: ConvertLeadModal (an ACCOUNT candidate is
 * pickable — C2.3) and NewContactDrawer (a CONTACT candidate is openable —
 * C3b). No surface renders a candidate row of its own (§15.10.11: reuse
 * wholesale, never a second renderer). The C1.1 company rule (candidateLabel)
 * and the why-copy (ruleLabel) stay in leadsFlow.ts so vitest pins them
 * without DOM.
 *
 * Looks (ADOPTION §8.4 / §5): a nested rounded-tile, rows as plain rows, the
 * one action per row as .k-btn-quiet — never a filled button inside a list.
 * The refusal form wears the attention tones (tokens.attention, the house
 * banner idiom) and names the refusal in its eyebrow, so it can never be
 * mistaken for the advisory tile; the two never render together.
 */
import type { ReactNode } from 'react'
import { Building2, UserRound, Check } from 'lucide-react'
import type { SalesMatchCandidate } from '../../../../api/types'
import { attention } from '../../../../theme/tokens'
import { candidateLabel, ruleLabel } from './leadsFlow'

/** What a surface offers on one candidate row, if anything. A TOGGLE (the
 *  convert pick) supplies pressedLabel and pressed; a plain action (open the
 *  existing contact) supplies neither. A row with no action is information. */
export interface CandidateAction {
  label: string
  onAct: () => void
  pressedLabel?: string
  pressed?: boolean
}

export type CandidateActionFor = (c: SalesMatchCandidate) => CandidateAction | undefined

export function CandidateRow({ c, action }: { c: SalesMatchCandidate; action?: CandidateAction }) {
  const { primary, secondary } = candidateLabel(c)
  const isAccount = c.type === 'account'
  const toggle = action?.pressedLabel !== undefined
  const pressed = toggle && action?.pressed === true
  return (
    <div className="flex items-start gap-3 py-2 border-b border-edge-row last:border-0">
      <div className="w-7 h-7 rounded-full bg-surface-panel border border-edge-row flex items-center justify-center text-ink-faint flex-shrink-0 mt-0.5">
        {isAccount ? <Building2 size={12} /> : <UserRound size={12} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-13 font-semibold text-ink-primary truncate">
          {isAccount ? primary : c.displayName}
        </div>
        {!isAccount && (primary !== '' || c.email) && (
          <div className="text-12 text-ink-secondary truncate">
            {primary !== '' ? primary : c.email}
            {primary !== '' && c.email ? ` · ${c.email}` : ''}
          </div>
        )}
        {/* C1.1: the raw text beneath ONLY when it differs from the canonical. */}
        {secondary !== '' && <div className="text-11 text-ink-faint truncate">also recorded as “{secondary}”</div>}
        <div className="text-11 text-ink-faint mt-0.5">{ruleLabel(c)}</div>
      </div>
      {action && (
        <button type="button" onClick={action.onAct}
          className={`k-btn-quiet inline-flex items-center gap-1 flex-shrink-0 ${pressed ? 'text-ink-primary' : ''}`}
          aria-pressed={toggle ? pressed : undefined}>
          {pressed ? <><Check size={12} />{` ${action.pressedLabel}`}</> : action.label}
        </button>
      )}
    </div>
  )
}

/** The per-surface words of the advisory tile's three honest states. */
export interface AdvisoryCopy {
  /** The tile's eyebrow. Absent = "Possible duplicates", the dedup surfaces'
   *  word (New Contact, Convert); a surface that SUGGESTS rather than warns
   *  (the contact editor's account picker, §25 L-4) names its own — a
   *  duplicate warning over "No account has a similar name" contradicts
   *  itself. */
  heading?: string
  checking: string
  /** The read FAILED — never rendered as "none" (C2.4). */
  unavailable: string
  empty: string
}

/** C2: the ADVISORY tile — informs, never gates. Contacts first, then
 *  accounts, each with WHY it matched; `footer` is the surface's own trailing
 *  line (the convert modal's "The deal will be linked to …"). */
export function AdvisoryChooser({ loading, error, candidates, actionFor, copy, footer }: {
  loading: boolean
  error: unknown
  candidates: SalesMatchCandidate[]
  actionFor: CandidateActionFor
  copy: AdvisoryCopy
  footer?: ReactNode
}) {
  const settled = !loading && !error
  const contacts = candidates.filter(c => c.type === 'contact')
  const accounts = candidates.filter(c => c.type === 'account')
  return (
    <div className="rounded-tile border border-edge-row bg-surface-panel px-4 py-3" data-testid="duplicate-chooser">
      <div className="k-eyebrow uppercase mb-1">{copy.heading ?? 'Possible duplicates'}</div>
      {loading && (
        <div className="text-12 text-ink-muted">{copy.checking}</div>
      )}
      {!loading && !!error && (
        <div className="text-12 text-ink-muted">{copy.unavailable}</div>
      )}
      {settled && candidates.length === 0 && (
        <div className="text-12 text-ink-muted">{copy.empty}</div>
      )}
      {settled && contacts.map(c => (
        <CandidateRow key={`c-${c.id}`} c={c} action={actionFor(c)} />
      ))}
      {settled && accounts.map(c => (
        <CandidateRow key={`a-${c.id}`} c={c} action={actionFor(c)} />
      ))}
      {footer}
    </div>
  )
}

/** C3: the REFUSAL form — the write path said no. The server's sentence is
 *  quoted verbatim, `hint` says these rows came from the server at submit
 *  (not the advisory check) and names the way forward, and "Create anyway"
 *  resubmits with createAnyway: true — the override the server records. */
export function RefusalChooser({
  eyebrow, message, hint, candidates, actionFor, footer, createAnywayNote, onCreateAnyway, createAnywayDisabled,
}: {
  eyebrow: string
  message: string
  hint: string
  candidates: SalesMatchCandidate[]
  actionFor: CandidateActionFor
  footer?: ReactNode
  createAnywayNote: string
  onCreateAnyway: () => void
  createAnywayDisabled: boolean
}) {
  const contacts = candidates.filter(c => c.type === 'contact')
  const accounts = candidates.filter(c => c.type === 'account')
  return (
    <div className="rounded-tile px-4 py-3" data-testid="duplicate-refusal" role="alert"
      style={{ background: attention['banner-bg'], border: `1px solid ${attention['banner-border']}` }}>
      <div className="k-eyebrow uppercase mb-1" style={{ color: attention['banner-text'] }}>{eyebrow}</div>
      <div className="text-12 text-ink-body">{message}</div>
      <div className="text-11 text-ink-faint mt-1 mb-1">{hint}</div>
      {contacts.map(c => (
        <CandidateRow key={`rc-${c.id}`} c={c} action={actionFor(c)} />
      ))}
      {accounts.map(c => (
        <CandidateRow key={`ra-${c.id}`} c={c} action={actionFor(c)} />
      ))}
      {footer}
      <div className="flex items-center justify-between gap-3 mt-3">
        <div className="text-11 text-ink-faint">{createAnywayNote}</div>
        <button type="button" onClick={onCreateAnyway} disabled={createAnywayDisabled}
          className="k-btn-secondary flex-shrink-0 disabled:opacity-60">
          Create anyway
        </button>
      </div>
    </div>
  )
}
